import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function OrderRtoManagement({ orderIdProp, inModal = false, onSuccess }: { orderIdProp?: string, inModal?: boolean, onSuccess?: () => void }) {
    const params = useParams();
    const id = orderIdProp || params.id;
    const toast = useToast();
    const [order, setOrder] = useState<any>(null);
    const [rtos, setRtos] = useState<RtoRecord[]>([]);
    const [rtoAgents, setRtoAgents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingRto, setEditingRto] = useState<RtoRecord | null>(null);
    const [formData, setFormData] = useState({
        order_item_id: '',
        rto_agent_id: '',
        registration_status: 'pending',
        registration_number: '',
        registration_date: '',
        rto_charges: ''
    });

    useEffect(() => {
        loadData();
    }, [id]);

    // Handle direct add for modal view
    useEffect(() => {
        if (inModal && rtos.length === 0 && !showForm && !editingRto) {
            handleAdd();
        }
    }, [inModal, rtos.length, showForm, editingRto]);

    const loadData = async () => {
        try {
            const [orderRes, rtosRes, agentsRes] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/rtos`),
                api.get('/rto_agents')
            ]);
            setOrder(orderRes.data);
            const rtosData = Array.isArray(rtosRes.data) ? rtosRes.data : (rtosRes.data.data || []);
            setRtos(rtosData);
            const agents = agentsRes.data.data || agentsRes.data || [];
            setRtoAgents(agents);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load RTO data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingRto(null);
        setFormData({
            order_item_id: '',
            rto_agent_id: '',
            registration_status: 'pending',
            registration_number: '',
            registration_date: '',
            rto_charges: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let response;
            if (editingRto) {
                response = await api.put(`/orders/${id}/rtos/${editingRto.id}`, formData);
            } else {
                response = await api.post(`/orders/${id}/rtos`, {
                    order_item_id: formData.order_item_id,
                    rto_agent_id: formData.rto_agent_id,
                    registration_status: formData.registration_status,
                    registration_number: formData.registration_number,
                    registration_date: formData.registration_date,
                    rto_charges: formData.rto_charges
                });
            }

            if (response.success) {
                toast.success(`RTO record ${editingRto ? 'updated' : 'created'} successfully`);
                resetForm();
                await loadData();
                if (onSuccess) onSuccess();
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving RTO record');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving RTO:', error);
            toast.error(error.message || 'An unexpected error occurred');
        }
    };

    const handleEdit = (rto: RtoRecord) => {
        setEditingRto(rto);
        setFormData({
            order_item_id: rto.order_item_id,
            rto_agent_id: rto.rto_agent_id || '',
            registration_status: rto.registration_status,
            registration_number: rto.registration_number || '',
            registration_date: rto.registration_date || '',
            rto_charges: rto.rto_charges?.toString() || ''
        });
        setShowForm(true);
    };

    const handleAdd = () => {
        setEditingRto(null);
        setFormData({
            order_item_id: order?.items?.length === 1 ? order.items[0].id : '',
            rto_agent_id: '',
            registration_status: 'pending',
            registration_number: '',
            registration_date: '',
            rto_charges: ''
        });
        setShowForm(true);
    };

    const handleDelete = async (rtoId: string) => {
        if (!confirm('Are you sure you want to delete this RTO record?')) return;
        try {
            await api.delete(`/orders/${id}/rtos/${rtoId}`);
            await loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error deleting RTO:', error);
        }
    };


    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            pending: 'bg-gray-100 text-gray-700',
            applied: 'bg-blue-100 text-blue-700',
            approved: 'bg-green-100 text-green-700',
            registered: 'bg-emerald-100 text-emerald-700',
            rejected: 'bg-red-100 text-red-700'
        };
        return badges[status] || badges.pending;
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!order) return <div className="p-8">Order not found.</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                {!inModal && (
                    <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold mb-4">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Order
                    </Link>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`${inModal ? 'text-2xl' : 'text-3xl'} font-black text-slate-900`}>RTO Registration</h1>
                        {!inModal && <p className="text-slate-600 mt-2">Order #{order.order_number}</p>}
                    </div>
                    <button
                        onClick={handleAdd}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all text-sm"
                    >
                        + Add RTO Registration
                    </button>
                </div>
            </div>

            {/* RTO Records Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Order Item</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">RTO Agent</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Reg. Number</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Reg. Date</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Charges</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rtos.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                    No RTO records found. Click "Add RTO Registration" to create one.
                                </td>
                            </tr>
                        ) : (
                            rtos.map((rto) => (
                                <tr key={rto.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-medium text-slate-900">{rto.order_item_id.slice(0, 8)}...</td>
                                    <td className="px-6 py-4 text-slate-700">{rto.rto_agent_name || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(rto.registration_status)}`}>
                                            {rto.registration_status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-700">{rto.registration_number || '-'}</td>
                                    <td className="px-6 py-4 text-slate-700">{rto.registration_date || '-'}</td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {rto.rto_charges ? `₹${rto.rto_charges.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleEdit(rto)}
                                            className="text-indigo-600 hover:text-indigo-800 font-bold mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(rto.id)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className={inModal ? "mb-8 bg-slate-50 rounded-2xl p-8 border border-slate-200" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50"}>
                    <div className={inModal ? "" : "bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
                        <h2 className="text-2xl font-black text-slate-900 mb-6">
                            {editingRto ? 'Edit RTO Registration' : 'Add RTO Registration'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {!editingRto && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Order Item</label>
                                    <select
                                        required
                                        value={formData.order_item_id}
                                        onChange={(e) => setFormData({ ...formData, order_item_id: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Select Order Item</option>
                                        {order.items?.map((item: any) => (
                                            <option key={item.id} value={item.id}>
                                                {item.product_name} - {item.product_unit_identifier || 'No SN'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">RTO Agent</label>
                                <select
                                    value={formData.rto_agent_id}
                                    onChange={(e) => setFormData({ ...formData, rto_agent_id: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select RTO Agent</option>
                                    {rtoAgents.map((agent) => (
                                        <option key={agent.id} value={agent.id}>{agent.agent_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Registration Status</label>
                                <select
                                    value={formData.registration_status}
                                    onChange={(e) => setFormData({ ...formData, registration_status: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="applied">Applied</option>
                                    <option value="approved">Approved</option>
                                    <option value="registered">Registered</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Registration Number</label>
                                    <input
                                        type="text"
                                        value={formData.registration_number}
                                        onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., DL01AB1234"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Registration Date</label>
                                    <input
                                        type="date"
                                        value={formData.registration_date}
                                        onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">RTO Charges</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.rto_charges}
                                    onChange={(e) => setFormData({ ...formData, rto_charges: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
                                >
                                    {editingRto ? 'Update' : 'Create'} RTO Record
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
